/**
 * Suggested questions for a supplier page, offered as chips in Nami
 * (`agent/page-answers.ts`; they used to sit behind an "Ask about this supplier"
 * button). Every answer cites either a contract section or a fact from the
 * supplier profile (`PROFILE_DOCUMENT_ID`, sections P.1 to P.5), which Nami turns
 * into a link to the agreement clause or the supplier tab that shows it.
 */
import type { ScriptedAnswer } from '@/demo/procurement/ask/types';
import { getClause } from '@/demo/procurement/data/clauses';
import { contractsForSupplier } from '@/demo/procurement/data/contracts';
import { getContractDocument } from '@/demo/procurement/data/contract-documents';
import type { ContractDocument } from '@/demo/procurement/data/contract-documents';
import { fmtDate, parseIsoDate } from '@/demo/procurement/data/dates';
import { fmtMoney, fmtNumber, fmtPct } from '@/demo/procurement/data/format';
import type { Contract, Supplier } from '@/demo/procurement/data/types';
import { alertsForSupplier } from './supplier360-cases';
import { narrativeFor } from './supplier360-narratives';

export const PROFILE_DOCUMENT_ID = 'profile';

// ---------------------------------------------------------------------------
// Generic suggested questions for suppliers without a hand-written narrative
// ---------------------------------------------------------------------------

function primaryContract(supplier: Supplier): Contract | undefined {
  const contracts = contractsForSupplier(supplier.id);
  return (
    contracts.find(
      (c) =>
        c.kind !== 'NDA' && c.kind !== 'Quality agreement' && c.kind !== 'SOW',
    ) ?? contracts[0]
  );
}

function findSection(
  doc: ContractDocument,
  pattern: RegExp,
): { ref: string; heading: string } | undefined {
  const match = doc.sections.find(
    (section) =>
      section.level === 2 &&
      section.paragraphs.length > 0 &&
      pattern.test(section.heading),
  );
  return match ? { ref: match.number, heading: match.heading } : undefined;
}

function termsQuestion(
  supplier: Supplier,
  contract: Contract,
): ScriptedAnswer | null {
  const doc = getContractDocument(contract.id);
  if (!doc) return null;
  const section = findSection(doc, /payment terms/i);
  if (!section) return null;
  const clause = getClause(`${contract.id}:${section.ref}`);
  return {
    question: `What are the payment terms with ${supplier.shortName}?`,
    aliases: [`When do we pay ${supplier.shortName}?`],
    thinking: [`Reading the payment clauses in ${contract.id}`],
    answer: `${supplier.shortName} is paid on **${contract.paymentTerms}** under ${contract.id} (§${section.ref}).${
      clause ? `\n\n${clause.detail}` : ''
    }\n\nThe invoice compliance check flags any invoice that states a different term.`,
    citations: [
      {
        documentId: contract.id,
        ref: section.ref,
        quote: clause?.quote,
      },
    ],
  };
}

function termQuestion(
  supplier: Supplier,
  contract: Contract,
): ScriptedAnswer | null {
  const doc = getContractDocument(contract.id);
  if (!doc) return null;
  const section = findSection(doc, /^(initial |evergreen )?term/i);
  const end = fmtDate(parseIsoDate(contract.endDate));
  const renewal = contract.autoRenew.renews
    ? `It auto-renews for ${contract.autoRenew.termMonths} months unless notice of non-renewal is given ${contract.autoRenew.noticeDays} days before the end.`
    : 'It does not renew automatically, so any extension needs a signed amendment.';
  const clause = section
    ? getClause(`${contract.id}:${section.ref}`)
    : undefined;
  return {
    question: `When does the ${supplier.shortName} agreement end?`,
    aliases: [`Does the ${supplier.shortName} agreement auto-renew?`],
    thinking: [`Reading the term clause in ${contract.id}`],
    answer: `${contract.id} runs from ${fmtDate(parseIsoDate(contract.effectiveDate))} to **${end}**. ${renewal}\n\nAnnual value is ${fmtMoney(contract.annualValue)} and the contract owner is on the Agreements tab.`,
    citations: section
      ? [
          {
            documentId: contract.id,
            ref: section.ref,
            quote: clause?.quote,
          },
        ]
      : [{ documentId: PROFILE_DOCUMENT_ID, ref: 'P.1' }],
  };
}

function noAgreementQuestion(supplier: Supplier): ScriptedAnswer {
  return {
    question: `Is there an agreement with ${supplier.shortName}?`,
    aliases: [`What contract covers ${supplier.shortName}?`],
    thinking: ['Searching the contract repository for this supplier'],
    answer:
      supplier.spendTtm > 0
        ? `No. There is no agreement on file for ${supplier.shortName}. Pillar spent ${fmtMoney(supplier.spendTtm)} over the last 12 months on one-off purchase orders, so price, delivery and quality terms are not contractually protected.`
        : `No. There is no agreement on file for ${supplier.shortName} yet. ${supplier.name} is ${supplier.segment === 'Prospect' ? 'a bidder in open competitive bids' : 'in onboarding'}.`,
    citations: [{ documentId: PROFILE_DOCUMENT_ID, ref: 'P.1' }],
  };
}

function performanceQuestion(supplier: Supplier): ScriptedAnswer {
  return {
    question: `How is ${supplier.shortName} performing?`,
    aliases: [
      `What is the ${supplier.shortName} scorecard?`,
      `Summarize ${supplier.shortName} performance`,
    ],
    thinking: [
      'Reading delivery, quality and service history for the last 12 months',
    ],
    answer: `${supplier.shortName} is rated **${supplier.riskLevel} risk** (score ${supplier.riskScore}). See the figures below.\n\n${
      supplier.otif !== null
        ? `- **${supplier.otifLabel ?? 'On time, in full'}.** ${fmtPct(supplier.otif)} over the last 12 months.\n`
        : ''
    }${
      supplier.ppm !== null
        ? `- **Defect rate.** ${fmtNumber(supplier.ppm)} defects per million.\n`
        : ''
    }- **Spend.** ${fmtMoney(supplier.spendTtm)} over the last 12 months.\n- **Risk note.** ${supplier.riskNote}`,
    citations: [
      { documentId: PROFILE_DOCUMENT_ID, ref: 'P.2' },
      { documentId: PROFILE_DOCUMENT_ID, ref: 'P.3' },
    ],
  };
}

function riskQuestion(supplier: Supplier): ScriptedAnswer {
  const alerts = alertsForSupplier(supplier.id);
  return {
    question: `What are the main risks with ${supplier.shortName}?`,
    aliases: [`Is ${supplier.shortName} a risk?`],
    thinking: ['Reading risk alerts, credit score and screening results'],
    answer: `${supplier.riskNote}\n\n${
      alerts.length > 0
        ? alerts.map((a) => `- **${a.id}.** ${a.title}.`).join('\n')
        : '- No open risk alerts.'
    }\n- **Credit.** Score ${supplier.creditScore} of 100${
      supplier.creditScorePrior === undefined
        ? ''
        : `, down from ${supplier.creditScorePrior}`
    }.`,
    citations: [{ documentId: PROFILE_DOCUMENT_ID, ref: 'P.3' }],
  };
}

function genericQuestions(supplier: Supplier): ScriptedAnswer[] {
  const contract = primaryContract(supplier);
  const first =
    contract === undefined
      ? noAgreementQuestion(supplier)
      : (termsQuestion(supplier, contract) ?? termQuestion(supplier, contract));
  const second =
    contract === undefined ? null : termQuestion(supplier, contract);
  const questions = [
    first,
    second,
    performanceQuestion(supplier),
    riskQuestion(supplier),
  ].filter((q): q is ScriptedAnswer => q !== null);
  // Drop duplicates (first and second can resolve to the same question).
  const seen = new Set<string>();
  return questions
    .filter((q) => {
      if (seen.has(q.question)) return false;
      seen.add(q.question);
      return true;
    })
    .slice(0, 3);
}

/** Three scripted questions: hand-written for story suppliers, generated otherwise. */
export function buildSuggestions(supplier: Supplier): ScriptedAnswer[] {
  return narrativeFor(supplier.id)?.questions ?? genericQuestions(supplier);
}
